import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Palette, MapPin, Settings, LogOut, ShoppingBag, ChevronRight, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/account", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/account/orders", icon: Package, label: "My Orders" },
  { to: "/account/designs", icon: Palette, label: "Saved Designs" },
  { to: "/account/addresses", icon: MapPin, label: "Addresses" },
  { to: "/account/settings", icon: Settings, label: "Account Settings" },
];

const DashboardLayout = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return <Navigate to="/signin" replace state={{ from: location }} />;

  const handleSignOut = () => {
    signOut();
    navigate("/");
  };

  const initials = `${(user.firstName || "D")[0]}${(user.lastName || "P")[0]}`.toUpperCase();
  const currentItem = NAV.slice().reverse().find((n) => location.pathname === n.to || (!n.end && location.pathname.startsWith(n.to))) || NAV[0];

  return (
    <main className="bg-zinc-50 min-h-screen">
      {/* Dashboard header strip */}
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen((s) => !s)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <nav className="text-xs text-zinc-500 flex items-center gap-2">
              <Link to="/" className="hover:text-rose-500">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/account" className="hover:text-rose-500">Account</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-zinc-900 font-medium">{currentItem.label}</span>
            </nav>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-900 bg-zinc-100 hover:bg-rose-500 hover:text-white px-4 py-2 rounded-md transition-colors">
            <ShoppingBag className="w-3.5 h-3.5" /> Continue shopping
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className={`lg:w-72 shrink-0 ${mobileOpen ? "block" : "hidden lg:block"}`}>
          <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <div className="p-6 bg-gradient-to-br from-rose-500 to-orange-500 text-white">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur border-2 border-white/30 flex items-center justify-center text-lg font-bold tracking-wide">{initials}</div>
                <div className="min-w-0">
                  <div className="font-bold truncate">{user.firstName} {user.lastName}</div>
                  <div className="text-xs text-white/80 truncate">{user.email}</div>
                </div>
              </div>
            </div>
            <ul className="py-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors border-l-4 ${isActive ? "bg-rose-50 text-rose-600 border-rose-500" : "text-zinc-700 border-transparent hover:bg-zinc-50 hover:text-zinc-900"}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="px-3 pt-3 mt-2 border-t border-zinc-100">
                <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-2 py-2.5 text-sm font-medium text-zinc-500 hover:text-rose-500 transition-colors rounded-md">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </li>
            </ul>
          </div>

          {/* Help card */}
          <div className="mt-4 bg-zinc-900 text-white rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-rose-400 font-bold mb-2">Need help?</div>
            <div className="text-sm leading-relaxed">Call our studio at <a href="tel:+17547551819" className="font-bold hover:underline">754-755-1819</a> or email <a href="mailto:orders@dplus.ai" className="font-bold hover:underline">orders@dplus.ai</a></div>
          </div>
        </aside>

        {/* Content */}
        <section className="flex-1 min-w-0">
          <Outlet />
        </section>
      </div>
    </main>
  );
};

export default DashboardLayout;
